import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, MessagesSquare, Target, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { queueCompletion } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { LevelBadge } from "@/components/LevelBadge";
import { VocabStep } from "@/components/lesson/VocabStep";
import { DialogueStep } from "@/components/lesson/DialogueStep";
import { QuizStep } from "@/components/lesson/QuizStep";
import { ResultView } from "@/components/lesson/ResultView";

const STEPS = [
  { label: "Vocabulaire", icon: BookOpen },
  { label: "Dialogue", icon: MessagesSquare },
  { label: "Situation", icon: Target },
];

export default function LessonPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleplay, setRoleplay] = useState(false);

  useEffect(() => {
    api.get(`/lessons/${id}`).then((r) => setLesson(r.data)).catch((e) => {
      toast.error(formatApiError(e));
      nav("/");
    });
  }, [id, nav]);

  const finish = async (answers) => {
    if (lesson.completed) return nav("/lecons");
    setSaving(true);
    try {
      const { data } = await api.post(`/lessons/${id}/complete`, { answers });
      setResult(data);
      refresh();
    } catch (e) {
      if (!e.response) {
        queueCompletion(id, answers);
        const score = lesson.situation.questions.filter((q, i) => q.answer_index === answers[i]).length;
        setResult({ score, total: answers.length, previous_level: lesson.level, new_level: lesson.level, level_changed: false, offline: true });
      } else toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!lesson)
    return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  if (saving)
    return <div className="py-20 grid place-items-center" data-testid="lesson-saving"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  if (result) return <ResultView result={result} />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} data-testid="lesson-back-btn" className="flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-5 h-5" /> Retour
        </button>
        <LevelBadge level={lesson.level} testId="lesson-level-badge" />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase leading-tight" data-testid="lesson-title">
        {lesson.title}
      </h1>
      {lesson.photo_url && (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${lesson.photo_url}`}
          alt="Photo du chantier"
          data-testid="lesson-photo"
          className="w-full max-h-72 object-cover rounded-2xl border-4 border-slate-900"
        />
      )}
      <div className="grid grid-cols-3 gap-2" data-testid="lesson-step-indicator">
        {STEPS.map(({ label, icon: Icon }, i) => (
          <button
            key={label}
            onClick={() => (lesson.completed || i <= step) && setStep(i)}
            data-testid={`lesson-step-${i}`}
            className={`rounded-xl py-2 flex flex-col items-center gap-1 text-xs font-bold uppercase border-2 transition-colors ${
              i === step ? "bg-orange-500 border-orange-500 text-white" : i < step ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"
            }`}
          >
            <Icon className="w-5 h-5" /> {label}
          </button>
        ))}
      </div>

      {step === 0 && <VocabStep vocabulary={lesson.vocabulary} />}
      {step === 1 && <DialogueStep dialogue={lesson.dialogue} onModeChange={setRoleplay} />}
      {step === 2 && (
        <QuizStep situation={lesson.situation} review={lesson.completed} savedAnswers={lesson.answers} onDone={finish} />
      )}

      {step < 2 && !roleplay && (
        <button
          onClick={() => { window.speechSynthesis?.cancel(); setStep(step + 1); window.scrollTo(0, 0); }}
          data-testid="lesson-next-step-btn"
          className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[.98] text-white font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-[background-color,transform]"
        >
          Suivant <ArrowRight className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
